// =============================
// Imports
// =============================

import { Typo } from '@mewo/components';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';

import Link from '../../../other/link';
import NoAuthLanguageSwitcher from '../../../other/noAuthLanguageSwitcher';
import Input from '../../../presentationals/inputs/input';
import SelectInput from '../../../presentationals/inputs/selectInput';
import MewoInput from '../../../presentationals/mewoInput';

import validator from '../../../../validators/auth/register';

import { buildExternalLinkTextNode } from '../../../../helpers/locales';
import { getSiteOrigin } from '../../../../helpers/misc';
import * as pth from '../../../../helpers/proptypes';

import { InputDouble, InputWrapper } from '../../global.styles';
import { Logo, Notification, Wrapper } from '../auth.styles';
import { RegisterButton, RegisterWrapper, SubTitle, Title, TosAgreement } from './styles';

// =============================
// Page
// =============================

const Register = ({ registerUser, territories, t }) => {
  const countryOptions = territories.map(({ code, name }) => ({ label: name, value: code }));

  return (
    <Wrapper>
      <RegisterWrapper>
        <Logo mx="auto" />

        <Title>{t('pages:register.title')}</Title>
        <SubTitle>{t('pages:register.subtitle')}</SubTitle>

        <Form onSubmit={registerUser}>
          {({ submitError, handleSubmit, submitting, values }) => (
            <form onSubmit={handleSubmit}>
              {submitError && (
                <Notification center type="error">
                  {submitError}
                </Notification>
              )}
              <InputWrapper>
                <InputDouble>
                  <Field
                    name="first_name"
                    component={MewoInput}
                    use={Input}
                    type="text"
                    label={t('common:form.first_name')}
                    validate={validator.first_name}
                  />
                  <Field
                    name="last_name"
                    component={MewoInput}
                    use={Input}
                    type="text"
                    label={t('common:form.last_name')}
                    validate={validator.last_name}
                  />
                </InputDouble>
                <InputDouble>
                  <Field
                    name="email"
                    component={MewoInput}
                    use={Input}
                    type="text"
                    label={t('common:form.email')}
                    validate={validator.email}
                  />
                  <Field
                    name="phone_number"
                    component={MewoInput}
                    use={Input}
                    type="text"
                    label={t('common:form.phone_number')}
                    validate={validator.phone_number}
                  />
                </InputDouble>
                <InputDouble>
                  <Field
                    name="company_name"
                    component={MewoInput}
                    use={Input}
                    type="text"
                    label={t('common:form.company_name')}
                    validate={validator.company_name}
                  />
                  <Field
                    name="job"
                    component={MewoInput}
                    use={Input}
                    type="text"
                    label={t('common:form.job')}
                    validate={validator.job}
                  />
                </InputDouble>
                <InputDouble>
                  <Field
                    name="company_type"
                    component={MewoInput}
                    use={SelectInput}
                    label={t('common:form.company_type.label')}
                    validate={validator.company_type}
                    options={[
                      { label: t('common:form.company_type.supervisor'), value: 'supervisor' },
                      { label: t('common:form.company_type.label-publisher'), value: 'label-publisher' },
                      { label: t('common:form.company_type.library'), value: 'library' },
                    ]}
                    renderOptionsVariant="classic"
                    renderValuesVariant="text"
                  />
                  <Field
                    name="country"
                    component={MewoInput}
                    use={SelectInput}
                    type="text"
                    label={t('common:form.country')}
                    validate={validator.country}
                    options={countryOptions}
                    searchable
                  />
                </InputDouble>
                <InputDouble>
                  <Field
                    name="password"
                    component={MewoInput}
                    use={Input}
                    type="password"
                    label={t('common:form.password')}
                    validate={validator.password}
                  />
                  <Field
                    name="password_confirmation"
                    component={MewoInput}
                    use={Input}
                    type="password"
                    label={t('common:form.password_confirmation')}
                    validate={validator.password_confirmation(
                      values.password,
                      t('common:form.password'),
                    )}
                  />
                </InputDouble>
              </InputWrapper>

              <RegisterButton type="submit" disabled={submitting}>
                {t('pages:register.register')}
              </RegisterButton>
              <TosAgreement>
                {buildExternalLinkTextNode(
                  t('pages:register.tos_you_agree_to'),
                  `${getSiteOrigin()}/static/mewo-tos.pdf`,
                )}
              </TosAgreement>
            </form>
          )}
        </Form>

        <Typo centered size={1.3}>
          {t('pages:register.already_registered')}
          {' '}
          <Link inline href="/">
            <Typo color="primary">{t('pages:register.signin')}</Typo>
          </Link>
        </Typo>
      </RegisterWrapper>

      <NoAuthLanguageSwitcher />
    </Wrapper>
  );
};

Register.propTypes = {
  registerUser: PropTypes.func.isRequired,
  territories: PropTypes.arrayOf(pth.territory.isRequired).isRequired,
  t: PropTypes.func.isRequired,
};

export default Register;
